// Typed client for the Booth API (backend/, Spring). Every call carries the
// Keycloak access token; a 401 ends the local session.

import { apiUrl, remoteConfig, setTurn } from '../config';
import { accessToken, dropSession } from '../auth/oidc';
import type { TierId } from '../booth/tiers';
import type { FaceBox, PoseAdjust } from '../booth/layout';
import type { BoothConfig } from '../net/protocol';

export interface User { id: string; username: string | null; displayName: string; avatarUrl: string | null; bio: string | null }
export type Level = 'NEW' | 'BLOOM' | 'CLOSE' | 'BESTIE';
export interface Friend { user: User; closeness: number; level: Level; since: string; /** 🔥 days (F2), 0 = none */ streak?: number }
export interface FriendRequest { id: string; from: User; to: User; message: string | null; createdAt: string }
export type Relation = 'SELF' | 'FRIENDS' | 'REQUEST_SENT' | 'REQUEST_RECEIVED' | 'BLOCKED' | 'NONE';
export interface RoomMember { user: User; role: 'OWNER' | 'MEMBER'; joinedAt: string }
export interface Room {
  id: string; code: string; title: string | null; kind: 'INSTANT' | 'PERSISTENT'; maxPeople: number;
  owner: User; members: RoomMember[]; settings: Partial<BoothConfig>; createdAt: string;
}
export interface RoomInvite { id: string; roomId: string; roomCode: string; roomTitle: string | null; inviter: User; createdAt: string }
export interface Photo {
  id: string; sessionId: string; kind: 'STRIP' | 'SHOT'; index: number; width: number; height: number; version: number;
  /** presigned ~720px WebP (feed page only); null/absent → use /api/photos/{id}/download */
  thumbUrl?: string | null;
}
export interface Session {
  id: string; roomId: string; hostId: string; config: BoothConfig; totalShots: number; startedAt: string;
  finalizedAt: string | null; participants: { user: User; slot: number }[]; photos: Photo[];
  /** v0.9: the AI has finished every saved photo (false while the GPU still works; null/absent = n/a) */
  ready?: boolean | null;
}
export type Visibility = 'FRIENDS' | 'CLOSE_FRIENDS' | 'PARTICIPANTS';
export type Emoji = 'HEART' | 'LAUGH' | 'WOW' | 'TOUCHED' | 'FIRE';
export interface Post {
  id: string; author: User; sessionId: string; caption: string | null; visibility: Visibility; photos: Photo[];
  people: User[]; commentCount: number; reactionCount: number; reactions: Partial<Record<Emoji, number>>;
  myReaction: Emoji | null; createdAt: string;
  /** friends see the post until then (author's 24 h setting); null = no limit */
  expiresAt?: string | null;
  /** profile timeline only: the profile's user is in the session but did not post it (F3) */
  collab?: boolean;
}

// ---- streaks, memories (F2, F4)
export interface Streak { user: User; days: number; atRisk: boolean; lastDay: string }
export interface OnThisDay { label: string; date: string; sessions: Session[] }
export interface CalendarDay { day: string; sessions: number; cover: { kind: 'PHOTO'; id: string; url?: string } | null }
export interface MemoryDay { sessions: Session[] }
export interface PersonTogether { user: User; sessionsTogether: number; firstAt: string; lastAt: string; coverPhotoId: string | null }
/** Settings → Quyền riêng tư */
export interface PrivacySettings { closenessEnabled: boolean; postsExpire: boolean; autoDeletePosts: boolean }
export interface FeedPage { items: Post[]; nextCursor: string | null }
export interface Profile { user: User; relation: Relation; streak?: number; sessionsTogether?: number }
export interface Comment { id: string; postId: string; author: User; parentId: string | null; body: string; createdAt: string; editedAt: string | null }
export type NotificationKind = 'FRIEND_REQUEST' | 'FRIEND_ACCEPTED' | 'ROOM_INVITE' | 'PHOTOS_READY' | 'POST_SHARED' | 'COMMENT' | 'REACTION'
  | 'REPLY' | 'FRIEND_POST';
export interface AppNotification {
  id: string; kind: NotificationKind; actorId: string | null; actorName: string | null; actorAvatar: string | null;
  /** REST: real values; FCM data: strings (JSON for nested values) */
  refId: string | null; data: Record<string, any>; readAt: string | null; createdAt: string;
}
export interface Reactor { user: Pick<User, 'id' | 'displayName' | 'avatarUrl'>; emoji: Emoji; at: string }
export interface Counts { bell: number; friends: number; rooms: number; feed: number }
export interface TopFriend { user: User; score: number; level: Level; sessionsTogether: number }
export type Format = 'JPEG' | 'PNG' | 'WEBP';
export interface Download { url: string; expiresAt: string; tier: string; format: Format; width: number; height: number }
export interface UploadTicket { uploadUrl: string; objectKey: string; expiresIn: number }

/** Photo reference used by the in-room gallery and the viewer. */
export interface PhotoRef { id: string; sessionId: string; kind: 'strip' | 'shot'; index: number; width?: number; height?: number }
export const toRef = (p: Photo): PhotoRef => ({
  id: p.id, sessionId: p.sessionId, kind: p.kind === 'STRIP' ? 'strip' : 'shot', index: p.index, width: p.width, height: p.height,
});

export class ApiError extends Error {
  constructor(readonly status: number, msg: string, readonly code = '') {
    super(msg);
  }
}

/** True when the Booth API answers (cached; failures retried). */
export async function apiAvailable() {
  return !!(await remoteConfig());
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const tok = await accessToken();
  if (!tok) throw new ApiError(401, 'Bạn cần đăng nhập');
  const r = await fetch(`${apiUrl()}${path}`, {
    method,
    headers: { authorization: `Bearer ${tok}`, ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) {
    dropSession();
    throw new ApiError(401, 'Phiên đăng nhập đã hết, đăng nhập lại nhé');
  }
  if (!r.ok) {
    let msg = r.statusText, code = '';
    try { const j = await r.json(); msg = j.message ?? msg; code = j.error ?? ''; } catch { /* not json */ }
    throw new ApiError(r.status, msg, code);
  }
  if (r.status === 204) return undefined as T;
  const text = await r.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** PUT a file to a presigned MinIO URL. */
export async function putFile(url: string, blob: Blob) {
  const r = await fetch(url, { method: 'PUT', body: blob, headers: { 'content-type': blob.type || 'application/octet-stream' } });
  if (!r.ok) throw new ApiError(r.status, `Tải ảnh lên thất bại (${r.status})`);
}

const qs = (o: Record<string, string | number | boolean | undefined>) =>
  new URLSearchParams(Object.entries(o).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString();

export const api = {
  /** Luật BVDLCN: tải dữ liệu của tôi / xoá tài khoản và toàn bộ dữ liệu */
  exportData: () => call<unknown>('GET', '/api/me/export'),
  deleteAccount: () => call<void>('DELETE', '/api/me', { confirm: 'XOA' }),
  // ---- me / users
  me: () => call<User>('GET', '/api/me'),
  mySettings: () => call<PrivacySettings>('GET', '/api/me/settings'),
  updateSettings: (s: Partial<Pick<PrivacySettings, 'postsExpire' | 'autoDeletePosts'>>) =>
    call<PrivacySettings>('PATCH', '/api/me/settings', s),
  updateMe: (p: Partial<Pick<User, 'username' | 'displayName' | 'bio'>>) => call<User>('PATCH', '/api/me', p),
  search: (q: string) => call<User[]>('GET', `/api/users/search?${qs({ q })}`),
  profile: (id: string) => call<Profile>('GET', `/api/users/${id}`),
  avatarTicket: () => call<UploadTicket>('POST', '/api/me/avatar/upload'),
  setAvatar: (objectKey: string) => call<User>('PUT', '/api/me/avatar', { objectKey }),

  // ---- streaks, memories
  streaks: () => call<Streak[]>('GET', '/api/streaks'),
  onThisDay: () => call<OnThisDay | null>('GET', '/api/memories/on-this-day'),
  memoryCalendar: (month: string) => call<CalendarDay[]>('GET', `/api/memories/calendar?${qs({ month })}`),
  memoryDay: (day: string) => call<MemoryDay>('GET', `/api/memories/day/${day}`),
  peopleTogether: () => call<PersonTogether[]>('GET', '/api/memories/people'),

  // ---- push devices (FCM)
  registerDevice: (token: string, platform: 'android' | 'web') => call<void>('POST', '/api/devices', { token, platform }),
  unregisterDevice: (token: string) => call<void>('DELETE', `/api/devices/${encodeURIComponent(token)}`),
  userPosts: (id: string, offset = 0) => call<Post[]>('GET', `/api/users/${id}/posts?${qs({ offset, limit: 20 })}`),

  // ---- friends
  friends: () => call<Friend[]>('GET', '/api/friends'),
  unfriend: (id: string) => call<void>('DELETE', `/api/friends/${id}`),
  requests: () => call<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>('GET', '/api/friend-requests'),
  sendRequest: (userId: string, message?: string) => call<{ status: 'REQUEST_SENT' | 'FRIENDS' }>('POST', '/api/friend-requests', { userId, message }),
  acceptRequest: (id: string) => call<void>('POST', `/api/friend-requests/${id}/accept`),
  declineRequest: (id: string) => call<void>('POST', `/api/friend-requests/${id}/decline`),
  cancelRequest: (id: string) => call<void>('DELETE', `/api/friend-requests/${id}`),
  block: (id: string) => call<void>('POST', `/api/blocks/${id}`),
  unblock: (id: string) => call<void>('DELETE', `/api/blocks/${id}`),
  closeFriends: () => call<string[]>('GET', '/api/close-friends'),
  setCloseFriends: (ids: string[]) => call<string[]>('PUT', '/api/close-friends', ids),
  topFriends: (limit = 5) => call<TopFriend[]>('GET', `/api/closeness/top?${qs({ limit })}`),
  setClosenessEnabled: (enabled: boolean) => call<void>('PUT', '/api/closeness/enabled', { enabled }),

  // ---- rooms
  rooms: () => call<Room[]>('GET', '/api/rooms'),
  room: (id: string) => call<Room>('GET', `/api/rooms/${id}`),
  createRoom: (title: string, kind: Room['kind']) => call<Room>('POST', '/api/rooms', { title, kind }),
  joinRoom: (code: string) => call<Room>('POST', '/api/rooms/join', { code }),
  leaveRoom: (id: string) => call<void>('DELETE', `/api/rooms/${id}/members/me`),
  invite: (roomId: string, userIds: string[]) => call<{ sent: number }>('POST', `/api/rooms/${roomId}/invites`, { userIds }),
  invites: () => call<RoomInvite[]>('GET', '/api/invites'),
  acceptInvite: (id: string) => call<Room>('POST', `/api/invites/${id}/accept`),
  declineInvite: (id: string) => call<void>('POST', `/api/invites/${id}/decline`),

  // ---- photo sessions
  createSession: (roomId: string, config: BoothConfig, totalShots: number) =>
    call<Session>('POST', `/api/rooms/${roomId}/sessions`, { config, totalShots }),
  joinSession: (id: string, slot: number) => call<Session>('POST', `/api/sessions/${id}/join`, { slot }),
  session: (id: string) => call<Session>('GET', `/api/sessions/${id}`),
  /** v0.9.7: one shot as a finished photo, once the GPU made it */
  shotFinal: (id: string, shot: number) => call<{ ready: boolean; url: string | null }>('GET', `/api/sessions/${id}/shots/${shot}/final`),
  /** v0.9: the booth opened — wakes the GPU compositor up while people pose */
  warmRoom: (roomId: string) => call<void>('POST', `/api/rooms/${roomId}/warm`),
  backgroundTicket: (id: string) => call<UploadTicket>('POST', `/api/sessions/${id}/background`),
  cutoutTicket: (id: string, shotIndex: number,
    meta: { face: FaceBox; adjust: PoseAdjust; masked: boolean; mirrored: boolean; noFace: boolean } & Record<string, unknown>) =>
    call<UploadTicket>('POST', `/api/sessions/${id}/cutouts`, { shotIndex, meta }),
  cutoutDone: (id: string, shot: number) => call<void>('POST', `/api/sessions/${id}/cutouts/${shot}/complete`),
  finalize: (id: string, config: BoothConfig) => call<Session>('POST', `/api/sessions/${id}/finalize`, { config }),
  library: (offset = 0) => call<Session[]>('GET', `/api/library?${qs({ offset, limit: 20 })}`),
  hideSession: (id: string) => call<void>('DELETE', `/api/library/${id}`),
  download: (photoId: string, tier: TierId, format: Format = 'JPEG', attachment = false) =>
    call<Download>('GET', `/api/photos/${photoId}/download?${qs({ tier: tier.toUpperCase(), format, attachment })}`),

  backgrounds: () => call<{ id: string; name: string; url: string }[]>('GET', '/api/backgrounds'),

  // ---- feed
  feed: (offset = 0) => call<Post[]>('GET', `/api/feed?${qs({ offset, limit: 15 })}`),
  /** cursor pages over a ranked snapshot (BA v1.1) */
  feedPage: (cursor: string | null, limit = 10) => call<FeedPage>('GET', `/api/feed/page?${qs({ cursor: cursor ?? undefined, limit })}`),
  post: (id: string) => call<Post>('GET', `/api/posts/${id}`),
  createPost: (sessionId: string, photoIds: string[], caption: string, visibility: Visibility) =>
    call<Post>('POST', '/api/posts', { sessionId, photoIds, caption, visibility }),
  deletePost: (id: string) => call<void>('DELETE', `/api/posts/${id}`),
  react: (id: string, emoji: Emoji) => call<Post>('PUT', `/api/posts/${id}/reaction`, { emoji }),
  reactors: (id: string, offset = 0, limit = 30) => call<Reactor[]>('GET', `/api/posts/${id}/reactors?${qs({ offset, limit })}`),
  unreact: (id: string) => call<Post>('DELETE', `/api/posts/${id}/reaction`),
  comments: (id: string) => call<Comment[]>('GET', `/api/posts/${id}/comments`),
  comment: (id: string, body: string, parentId?: string) => call<Comment>('POST', `/api/posts/${id}/comments`, { body, parentId }),
  editComment: (id: string, body: string) => call<Comment>('PATCH', `/api/comments/${id}`, { body }),
  deleteComment: (id: string) => call<void>('DELETE', `/api/comments/${id}`),

  // ---- notifications
  notifications: () => call<AppNotification[]>('GET', '/api/notifications?limit=60'),
  unreadCount: () => call<{ count: number }>('GET', '/api/notifications/unread-count'),
  /** tab badges: bell = unread notifications, friends = pending requests, rooms = room invites, feed = new posts */
  counts: () => call<Counts>('GET', '/api/notifications/counts'),
  markRead: (ids?: string[]) => call<void>('POST', '/api/notifications/read', ids ? { ids } : {}),

  /** Short-lived TURN credentials for this user (if the server runs TURN). */
  async loadTurn() {
    if (!(await remoteConfig())?.turn) return setTurn(null);
    try {
      const t = await call<{ urls: string[]; username: string; credential: string }>('GET', '/api/turn');
      setTurn({ urls: t.urls, username: t.username, credential: t.credential });
    } catch {
      setTurn(null);
    }
  },
};

// ------------------------------------------------------------------ small caches

const urlCache = new Map<string, { url: string; until: number }>();

/**
 * v0.9.6: the final photo for the result screen — HD without the PREVIEW watermark while
 * downloads are free (no paywall), else the watermarked preview.
 */
export async function finalUrl(p: { id: string; version?: number }, paywall: boolean): Promise<string> {
  if (paywall) return previewUrl(p);
  const key = `${p.id}:${p.version ?? 0}:hd`;
  const c = urlCache.get(key);
  if (c && c.until > Date.now()) return c.url;
  const d = await api.download(p.id, 'hd', 'JPEG');
  urlCache.set(key, { url: d.url, until: new Date(d.expiresAt).getTime() - 60_000 });
  return d.url;
}

/** Presigned preview URL for <img>, cached until shortly before it expires. */
export async function previewUrl(p: { id: string; version?: number }): Promise<string> {
  const key = `${p.id}:${p.version ?? 0}`;
  const c = urlCache.get(key);
  if (c && c.until > Date.now()) return c.url;
  const d = await api.download(p.id, 'preview', 'JPEG');
  urlCache.set(key, { url: d.url, until: new Date(d.expiresAt).getTime() - 60_000 });
  return d.url;
}

/**
 * Server-relative paths (e.g. avatarUrl "/api/avatars/{id}?v=3") are resolved against
 * the configured API origin; absolute http(s) URLs pass through; anything else → null.
 */
export function resolveApiUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/') && !u.startsWith('//')) return `${apiUrl()}${u}`;
  return null;
}
